import {
  DragEvent,
  MouseEvent,
  RefObject,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api, {
  DocumentSignFieldInput,
  DocumentSignFieldResponse,
  DocumentSignFileResponse,
  DocumentSignRecipientResponse,
} from '@/lib/api';
import { useFields, useRecipients, useSetFields } from '../hooks';
import { FieldOverlay, type DraftField } from './FieldOverlay';
import { FieldPalette, FIELD_DRAG_MIME, type FieldType } from './FieldPalette';
import { PdfPageView, usePdfDocument } from './PdfPageView';
import {
  EACH_RECIPIENT_COLOR,
  EACH_RECIPIENT_LABEL,
  getRecipientColor,
  type RecipientColor,
} from './recipientColors';
import { DEFAULT_FIELD_FRAC, clampFieldPlacement, isPlaceableFieldType } from './fieldPlacement';

interface FieldsEditorProps {
  envelopeId: string;
  files: DocumentSignFileResponse[];
  readOnly?: boolean;
  /**
   * A bulk send. Its one recipient is the "Each recipient" placeholder —
   * every field placed is copied onto each person's own envelope at send.
   */
  bulk?: boolean;
}

/**
 * Imperative handle the envelope builder uses to flush pending field
 * placements when the user navigates (Back / Review and send).
 */
export interface FieldsEditorHandle {
  /**
   * Flush pending placements. Resolves false when the save failed (the error
   * is shown in the editor) — the builder must not leave, or switch how the
   * envelope is sent, on top of placements the server never received.
   */
  save: () => Promise<boolean>;
}

interface FieldsEditorTabHandle {
  save: () => Promise<boolean>;
}

/** Max width (CSS px) at which a page is rendered. Pages shrink below this to
 *  fit a narrow column, but never upscale past it — keeps them crisp and
 *  sensibly sized on wide screens. */
const PAGE_RENDER_WIDTH = 720;

let scratchCounter = 0;
function nextScratchKey(): string {
  scratchCounter += 1;
  return `new-${Date.now()}-${scratchCounter}`;
}

function toDraft(field: DocumentSignFieldResponse): DraftField {
  return {
    key: field.fieldId,
    recipientId: field.recipientId,
    type: field.type,
    page: field.page,
    x: field.x,
    y: field.y,
    w: field.w,
    h: field.h,
    required: field.required,
  };
}

/**
 * Translucent, field-sized preview that rides the cursor while a field type is
 * armed, so the user sees what — and how big — the next click will drop. It
 * only shows while the pointer is over `boundsRef` (the document column), is
 * centred on the cursor to match placeField's centre-on-click maths, and is
 * pointer-events:none so it never intercepts the click that places the field.
 */
function FieldGhostLayer({
  type,
  boundsRef,
  pageWidth,
}: {
  type: FieldType;
  boundsRef: RefObject<HTMLElement | null>;
  pageWidth: number;
}) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    function onMove(e: PointerEvent) {
      const el = boundsRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const inside =
        e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      setPos(inside ? { x: e.clientX, y: e.clientY } : null);
    }
    // Hide the preview while a native drag is underway — the browser shows its
    // own drag image, and pointermove is suspended until the drag ends.
    function hide() {
      setPos(null);
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('dragstart', hide);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('dragstart', hide);
    };
  }, [boundsRef]);

  if (!pos) return null;
  const frac = DEFAULT_FIELD_FRAC[type];
  const w = frac.w * pageWidth;
  // Height fractions are relative to the (taller) page; approximate the page
  // aspect so the preview is close to the real placed size.
  const h = frac.h * pageWidth * 1.3;
  return createPortal(
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        left: pos.x - w / 2,
        top: pos.y - h / 2,
        width: w,
        height: h,
        pointerEvents: 'none',
        zIndex: 50,
        border: '1px dashed #2563eb',
        background: 'rgba(37,99,235,0.15)',
        borderRadius: 4,
      }}
    />,
    document.body,
  );
}

interface FieldsEditorTabProps {
  envelopeId: string;
  file: DocumentSignFileResponse;
  recipients: DocumentSignRecipientResponse[];
  readOnly?: boolean;
  bulk?: boolean;
}

/**
 * Per-file field placement editor. The user picks a recipient + a field type,
 * then clicks on a page to place a field. Placements auto-save — they are
 * flushed when the file tab changes and when the builder navigates; there is
 * no explicit save button.
 */
const FieldsEditorTab = forwardRef<FieldsEditorTabHandle, FieldsEditorTabProps>(
  function FieldsEditorTab({ envelopeId, file, recipients, readOnly, bulk = false }, ref) {
    const { fields, loading } = useFields(envelopeId, file.fileId);
    const setFields = useSetFields(envelopeId, file.fileId);

    const [drafts, setDrafts] = useState<DraftField[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [activeRecipientId, setActiveRecipientId] = useState<string>(
      recipients[0]?.recipientId ?? '',
    );
    const [activeType, setActiveType] = useState<FieldType | null>(null);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const sectionRef = useRef<HTMLElement>(null);
    const [pageWidth, setPageWidth] = useState(PAGE_RENDER_WIDTH);

    // Stable mapping from recipientId → color index, ordered by routing order.
    const recipientIndexById = useMemo(() => {
      const map = new Map<string, number>();
      [...recipients]
        .sort((a, b) => a.routingOrder - b.routingOrder)
        .forEach((r, i) => map.set(r.recipientId, i));
      return map;
    }, [recipients]);

    const colorFor = (recipientId: string): RecipientColor =>
      bulk ? EACH_RECIPIENT_COLOR : getRecipientColor(recipientIndexById.get(recipientId) ?? 0);
    const labelFor = (r: DocumentSignRecipientResponse | undefined): string =>
      bulk ? EACH_RECIPIENT_LABEL : r?.name || r?.email || 'Recipient';

    useEffect(() => {
      if (loading) return;
      if (!isDirty) {
        setDrafts(fields.map(toDraft));
      }
    }, [fields, loading, isDirty]);

    useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          const { downloadUrl } = await api.getDocumentSignFileDownloadUrl(envelopeId, file.fileId);
          if (!cancelled) setPdfUrl(downloadUrl);
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Failed to load PDF');
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [envelopeId, file.fileId]);

    useEffect(() => {
      if (recipients.length > 0 && !recipients.find((r) => r.recipientId === activeRecipientId)) {
        setActiveRecipientId(recipients[0].recipientId);
      }
    }, [recipients, activeRecipientId]);

    // Fit pages to the scroll column so the document is fully visible left↔right
    // instead of forcing a horizontal scrollbar. `scrollbar-gutter: stable` on
    // the section keeps clientWidth steady when the vertical scrollbar toggles,
    // which avoids a measure→resize feedback loop. Placements are fractional, so
    // a width change re-flows fields without moving them.
    useEffect(() => {
      const node = sectionRef.current;
      if (!node || typeof ResizeObserver === 'undefined') return;

      const measure = () => {
        // clientWidth excludes the (gutter-reserved) scrollbar; drop the pr-1
        // padding plus a 1px guard so subpixel rounding can't re-trigger overflow.
        const available = Math.floor(node.clientWidth) - 5;
        if (available > 0) setPageWidth(Math.min(PAGE_RENDER_WIDTH, available));
      };

      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      return () => observer.disconnect();
    }, []);

    const { doc: pdfDoc, error: pdfError, stalled: pdfStalled } = usePdfDocument(pdfUrl);
    const pageCount = file.pageCount ?? pdfDoc?.numPages ?? 0;

    /** Flush pending placements. A no-op when nothing changed. */
    const save = useCallback(async (): Promise<boolean> => {
      if (!isDirty) return true;
      try {
        const payload: DocumentSignFieldInput[] = drafts.map((d) => ({
          recipientId: d.recipientId,
          type: d.type,
          page: d.page,
          x: d.x,
          y: d.y,
          w: d.w,
          h: d.h,
          required: d.required ?? true,
        }));
        await setFields.mutateAsync(payload);
        setIsDirty(false);
        setError(null);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save fields');
        return false;
      }
    }, [drafts, isDirty, setFields]);

    useImperativeHandle(ref, () => ({ save }), [save]);

    /** Add a field; returns whether one was actually placed. */
    function placeField(type: FieldType, page: number, xFrac: number, yFrac: number): boolean {
      if (readOnly) return false;
      if (!activeRecipientId) return false;
      const def = DEFAULT_FIELD_FRAC[type];
      if (!def) return false; // guards a malformed drag payload
      const { x, y, w, h } = clampFieldPlacement(xFrac, yFrac, def);
      const key = nextScratchKey();
      setDrafts((prev) => [
        ...prev,
        { key, recipientId: activeRecipientId, type, page, x, y, w, h, required: true },
      ]);
      setSelectedKey(key);
      setIsDirty(true);
      return true;
    }

    function updateField(key: string, patch: Partial<DraftField>) {
      setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));
      setIsDirty(true);
    }

    function removeField(key: string) {
      setDrafts((prev) => prev.filter((d) => d.key !== key));
      if (selectedKey === key) setSelectedKey(null);
      setIsDirty(true);
    }

    function handlePageClick(e: MouseEvent<HTMLDivElement>, page: number, w: number, h: number) {
      if (readOnly) return;
      // No type armed → treat the page-background click as "deselect."
      // FieldOverlay stops propagation on its own click handlers, so this
      // handler only fires for genuine background clicks. Without this,
      // a placed field would stay selected forever (or until another
      // field is clicked), which is surprising — every editor convention
      // expects an empty-space click to dismiss the selection.
      if (!activeType) {
        setSelectedKey(null);
        return;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      const xFrac = (e.clientX - rect.left) / w;
      const yFrac = (e.clientY - rect.top) / h;
      // Most fields are one-per-document-per-recipient — disarm after a
      // placement so a stray next click doesn't drop a duplicate.
      if (placeField(activeType, page, xFrac, yFrac)) {
        setActiveType(null);
      }
    }

    /** Drop a field dragged straight from the palette onto a page. */
    function handlePageDrop(e: DragEvent<HTMLDivElement>, page: number, w: number, h: number) {
      if (readOnly) return;
      e.preventDefault();
      const dropped = e.dataTransfer.getData(FIELD_DRAG_MIME);
      if (!isPlaceableFieldType(dropped)) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const xFrac = (e.clientX - rect.left) / w;
      const yFrac = (e.clientY - rect.top) / h;
      placeField(dropped, page, xFrac, yFrac);
    }

    if (pdfError || error) {
      return <p className="text-sm text-destructive">{pdfError ?? error}</p>;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
        <aside className="space-y-4 self-start">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Recipient
            </h3>
            <Select
              value={activeRecipientId}
              onValueChange={setActiveRecipientId}
              disabled={readOnly}
            >
              <SelectTrigger aria-label="Active recipient">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {recipients.map((r) => {
                  const color = colorFor(r.recipientId);
                  return (
                    <SelectItem key={r.recipientId} value={r.recipientId}>
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block w-2 h-2 rounded-sm"
                          style={{ background: color.swatch }}
                          aria-hidden="true"
                        />
                        {bulk ? EACH_RECIPIENT_LABEL : r.name || r.email || r.recipientId}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Field type
            </h3>
            <FieldPalette
              selectedType={activeType}
              onSelect={(t) => setActiveType((cur) => (cur === t ? null : t))}
              pageWidth={pageWidth}
            />
          </div>
        </aside>

        <section
          ref={sectionRef}
          className="space-y-4 max-h-[75vh] overflow-y-auto pr-1 [scrollbar-gutter:stable]"
        >
          {pdfDoc === null ? (
            // A stall reads as a hang otherwise: this editor already said
            // "Loading…" forever, with nothing to say whether it was still
            // trying.
            <p className="text-sm text-muted-foreground">
              {pdfStalled
                ? 'This document is taking longer than usual to open.'
                : 'Loading document…'}
            </p>
          ) : (
            Array.from({ length: pageCount }, (_, i) => i + 1).map((pageNumber) => (
              <PdfPageView
                key={pageNumber}
                pdfDoc={pdfDoc}
                pageNumber={pageNumber}
                width={pageWidth}
              >
                {({ width, height }) => (
                  <div
                    className={!readOnly && activeType ? 'cursor-crosshair' : ''}
                    style={{ width, height }}
                    onClick={(e) => handlePageClick(e, pageNumber, width, height)}
                    onDragOver={readOnly ? undefined : (e) => e.preventDefault()}
                    onDrop={
                      readOnly ? undefined : (e) => handlePageDrop(e, pageNumber, width, height)
                    }
                    data-testid={`pdf-overlay-${pageNumber}`}
                  >
                    {drafts
                      .filter((d) => d.page === pageNumber)
                      .map((d) => {
                        const recipient = recipients.find((r) => r.recipientId === d.recipientId);
                        return (
                          <FieldOverlay
                            key={d.key}
                            field={d}
                            recipientIndex={recipientIndexById.get(d.recipientId) ?? 0}
                            recipientName={labelFor(recipient)}
                            color={colorFor(d.recipientId)}
                            pageWidth={width}
                            pageHeight={height}
                            selected={selectedKey === d.key}
                            readOnly={readOnly}
                            onSelect={() => setSelectedKey(d.key)}
                            onChange={(next) => updateField(d.key, next)}
                            onRemove={() => removeField(d.key)}
                          />
                        );
                      })}
                  </div>
                )}
              </PdfPageView>
            ))
          )}
        </section>

        {activeType && !readOnly ? (
          <FieldGhostLayer type={activeType} boundsRef={sectionRef} pageWidth={pageWidth} />
        ) : null}
      </div>
    );
  },
);

/**
 * File-tabbed field editor. Each file is its own mounted tab; switching tabs
 * (and navigating away) flushes the active tab's placements first.
 */
export const FieldsEditor = forwardRef<FieldsEditorHandle, FieldsEditorProps>(function FieldsEditor(
  { envelopeId, files, readOnly = false, bulk = false },
  ref,
) {
  const sourceFiles = useMemo(() => files.filter((f) => f.kind === 'source'), [files]);
  const { recipients } = useRecipients(envelopeId);

  const [activeFileId, setActiveFileId] = useState<string | null>(null);
  const tabRef = useRef<FieldsEditorTabHandle>(null);

  useEffect(() => {
    if (sourceFiles.length === 0) {
      setActiveFileId(null);
      return;
    }
    if (!activeFileId || !sourceFiles.find((f) => f.fileId === activeFileId)) {
      setActiveFileId(sourceFiles[0].fileId);
    }
  }, [sourceFiles, activeFileId]);

  useImperativeHandle(
    ref,
    () => ({
      save: async () => (await tabRef.current?.save()) ?? true,
    }),
    [],
  );

  // Flush the current tab before switching — each tab is a separate mounted
  // instance, so its drafts would otherwise be lost on unmount.
  async function selectFile(fileId: string) {
    if (fileId === activeFileId) return;
    // Stay on this file if its placements could not be saved — leaving would
    // unmount the tab and lose them.
    if ((await tabRef.current?.save()) === false) return;
    setActiveFileId(fileId);
  }

  if (recipients.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Add at least one recipient before placing fields.
      </p>
    );
  }

  if (sourceFiles.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Upload a document before placing fields.</p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 border-b">
        {sourceFiles.map((file) => (
          <button
            key={file.fileId}
            type="button"
            onClick={() => void selectFile(file.fileId)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${
              activeFileId === file.fileId
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {file.fileName}
          </button>
        ))}
      </div>

      {activeFileId ? (
        <FieldsEditorTab
          key={activeFileId}
          ref={tabRef}
          envelopeId={envelopeId}
          file={sourceFiles.find((f) => f.fileId === activeFileId)!}
          recipients={recipients}
          readOnly={readOnly}
          bulk={bulk}
        />
      ) : null}
    </div>
  );
});
